import { useMutation, useQuery } from "@tanstack/react-query";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice } from "../parts";
import { ReviewForm } from "./review/ReviewForm";
import { reviewPayload, type ReviewFormValues } from "./review/reviewForm";
import { productReviewState, type ProductReviewPage } from "./review/productReview";

// /product/purchase/<purchase-id>/review: the customer reviews a product
// they bought, from the review reminder email. Port of v1's page on
// ReviewPageShell, with the booking review's form: the purchase id is what
// lets them in (GET /widget/pages/product-purchases/:id/review, POST
// /widget/product-purchases/:id/review).

export function ProductReviewView({ ctx, purchaseId }: { ctx: ViewContext; purchaseId: string }) {
  const id = encodeURIComponent(purchaseId);
  const page = useQuery({
    queryKey: ["booking", "product-review", ctx.api, purchaseId],
    queryFn: () => bookingJson<ProductReviewPage>(`${ctx.api}/widget/pages/product-purchases/${id}/review`),
    retry: (count, error) => count < 2 && !(error instanceof BookingApiError),
  });
  const submit = useMutation({
    mutationFn: (values: ReviewFormValues) => sendJson(`${ctx.api}/widget/product-purchases/${id}/review`, "POST", reviewPayload(values)),
  });

  if (page.isPending) return <BookingLoading rows={2} />;
  const notFound = page.isError && page.error instanceof BookingApiError && page.error.status === 404;
  if (page.isError && !notFound) {
    return <BookingProblem title="This review page couldn’t be loaded" message="Please try again in a moment." onRetry={() => void page.refetch()} />;
  }

  const state = notFound ? "not-mine" : productReviewState(page.data, ctx.businessId);
  if (state === "not-mine") {
    return (
      <BookingProblem
        title="This purchase doesn't seem to belong to you"
        message="Did you really buy this? If so, please contact us."
        action={
          <a className="text-sm font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.contact()}>
            Contact us
          </a>
        }
      />
    );
  }

  if (submit.isSuccess || state === "reviewed") {
    return (
      <div className="mx-auto max-w-lg space-y-3 py-8 text-center">
        <h1 className="text-2xl font-semibold">Thank you for your review!</h1>
        <p className="text-muted-foreground">Your review will help others find the best products.</p>
        <a className="font-medium text-primary underline-offset-4 hover:underline" href={ctx.links.site("/shop")}>
          Show me more products
        </a>
      </div>
    );
  }

  const { purchase, product } = page.data!;
  return (
    <div className="mx-auto max-w-[500px] space-y-6 py-4">
      <h1 className="text-center text-2xl font-semibold">Please leave us a review for {product?.name ?? "your purchase"}</h1>
      {submit.isError && <Notice tone="error">{messageOf(submit.error, "Failed to submit the review. Please try again.")}</Notice>}
      <ReviewForm defaultName={purchase?.buyerName ?? ""} submitting={submit.isPending} onSubmit={(v) => submit.mutate(v)} />
    </div>
  );
}
